import { IClient, getClient } from '@libs/data-client'
import { Noop } from '@utils/typescript'

export type TextToSpeechParams = {
  id: string
  text: string
}

export type TextToSpeechStreamCallbacks = {
  onStream: (chunk: Uint8Array, id: string) => void
  onStreamStart: Noop
  onStreamEnd: Noop
  onStreamAbort: Noop
}

export class TextToSpeechAPI {
  private client: IClient

  constructor(client: IClient) {
    this.client = client
  }

  /**
   * Fetches synthesized audio for the given id and text (non-streaming).
   * The server caches audio by id; same article returns cached audio without calling the TTS backend again.
   */
  async synthesize(params: TextToSpeechParams, signal?: AbortSignal) {
    const formData = new FormData()
    formData.append('id', params.id)
    formData.append('text', params.text)

    const response = await this.client.post<FormData, Blob>('', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      signal,
    })

    return response.data
  }

  /**
   * Fetches synthesized audio via streaming - audio starts playing as chunks arrive.
   * Use this for better UX as audio begins playing before full download completes.
   */
  async synthesizeStream(
    params: TextToSpeechParams,
    callbacks: TextToSpeechStreamCallbacks,
    signal?: AbortSignal
  ): Promise<Blob> {
    const { onStream, onStreamStart, onStreamEnd, onStreamAbort } = callbacks

    const formData = new FormData()
    formData.append('id', params.id)
    formData.append('text', params.text)

    const response = await this.client.post<
      FormData,
      ReadableStream<Uint8Array>
    >(
      '', // remove:
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'stream',
        adapter: 'fetch',
        signal,
      }
    )

    const reader = response.data.getReader()

    const processStream = async (
      streamReader: ReadableStreamDefaultReader<Uint8Array>,
      controller: ReadableStreamDefaultController<Uint8Array>
    ) => {
      try {
        const { done, value } = await streamReader.read()

        if (done) {
          controller.close()
          onStreamEnd()
          return
        }

        onStream(value, params.id)
        controller.enqueue(value)

        await processStream(streamReader, controller)
      } catch (error) {
        if (signal?.aborted) {
          onStreamAbort()
          console.info(
            `[TextToSpeechAPI] Stream for id ${params.id} was aborted`
          )
          controller.close()
        } else {
          controller.error(error)
          throw error
        }
      }
    }

    const stream = new ReadableStream<Uint8Array>({
      start(controller) {
        onStreamStart()
        void processStream(reader, controller)
      },
    })

    return new Response(stream).blob()
  }
}

export const textToSpeechApi = new TextToSpeechAPI(
  getClient('text-to-speech', {
    noVersioning: true,
    instance: 'localClientInstance',
  })
)
